import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './NewCustomerForm.css';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { API_URL } from '../../utils/auth';
import Auth from '../../utils/auth';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const NewCustomerForm = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const workspaceId = Auth.getWorkspaceId();

  const [formData, setFormData] = useState({
    workspaceId: workspaceId || '',
    companyId: '',
    firstName: '',
    lastName: '',
    icp: '',
    touchpointFrequency: '',
    onboardingDate: new Date().toISOString(),
    phoneNumber: '',
    email: '',
    address: '',
    office: '',
    billingStatus: '',
    netPromoterScore: '',
    clientEffortScores: '',
    monthlyFeeSubscription: '',
    oneTimeOnboardingFee: '',
  });

  const [errors, setErrors] = useState({});
  const [isNewCompanyOpen, setIsNewCompanyOpen] = useState(false);
  const [newCompanyData, setNewCompanyData] = useState({ name: '' });
  const [companies, setCompanies] = useState([]); 

  useEffect(() => {
    const fetchCompanies = async () => {
      if (!workspaceId) {
        console.error('Workspace ID is missing.');
        toast.error('Workspace not identified.');
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/utils/get-companies?workspaceId=${workspaceId}`);
        setCompanies(response.data); 
      } catch (error) {
        console.error('Error fetching companies:', error.response?.data || error.message);
        toast.error('Error loading companies.');
      }
    };

    fetchCompanies();

    setFormData((prevData) => ({
      ...prevData,
      onboardingDate: new Date().toISOString(),
    }));
  }, [isOpen, workspaceId]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
  
    if (name === 'phoneNumber') {
      if (value.length < formData.phoneNumber.length) {
        setFormData({ ...formData, [name]: value });
        return;
      }
  
      const cleanedValue = value.replace(/[^\d+\-()\s]/g, '');
  
      setFormData({
        ...formData,
        [name]: cleanedValue, 
      });
      return;
    }

    if (['netPromoterScore', 'clientEffortScores', 'monthlyFeeSubscription', 'oneTimeOnboardingFee'].includes(name)) {
      if (/^\d*$/.test(value)) { 
        setFormData({
          ...formData,
          [name]: value,
        });
      }
      return;
    }

    if (name === 'email') {
      setFormData({
        ...formData,
        [name]: value,
      });
      return;
    }

    if (name === 'companyId') {
      setFormData({
        ...formData,
        companyId: value, 
      });
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const newErrors = {};
    
    // Required fields validation
    if (!formData.firstName) newErrors.firstName = true;
    if (!formData.lastName) newErrors.lastName = true;
    if (!formData.email || !emailRegex.test(formData.email)) newErrors.email = true;
    if (!formData.companyId) newErrors.companyId = true;
    if (!formData.icp) newErrors.icp = true;
    if (!formData.touchpointFrequency) newErrors.touchpointFrequency = true;
    if (!formData.billingStatus) newErrors.billingStatus = true;
    
    if (Object.keys(newErrors).length > 0) {
      toast.warning('Please fill in all required fields marked with *.');
      setErrors(newErrors);
      return;
    }

    const payload = {
      workspaceId: formData.workspaceId,
      companyId: formData.companyId, 
      firstName: formData.firstName,
      lastName: formData.lastName,
      clientRetentionScore: formData.clientRetentionScore ? parseFloat(formData.clientRetentionScore) : 0,
      icp: formData.icp,
      touchpointFrequency: formData.touchpointFrequency,
      onboardingDate: new Date(formData.onboardingDate).toISOString(),
      phoneNumber: formData.phoneNumber,
      email: formData.email,
      address: formData.address,
      office: formData.office || 'N/A',
      netPromoterScore: formData.netPromoterScore ? parseInt(formData.netPromoterScore, 10) : 0, 
      clientEffortScores: formData.clientEffortScores ? parseInt(formData.clientEffortScores, 10) : 0, 
      billingStatus: formData.billingStatus,
      monthlyFeeSubscription: formData.monthlyFeeSubscription ? parseInt(formData.monthlyFeeSubscription, 10) : 0,
      oneTimeOnboardingFee: formData.oneTimeOnboardingFee ? parseInt(formData.oneTimeOnboardingFee, 10) : 0,
      archived: formData.archived || 'false'
    };
  
    console.log('Payload being sent:', payload); 
  
    try {
      const response = await axios.post(`${API_URL}/clients/clients`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      toast.success('Client created successfully!');
      navigate('/clients');
      setFormData({
        workspaceId: workspaceId || '',
        companyId: '', 
        firstName: '',
        lastName: '',
        icp: '',
        touchpointFrequency: '',
        onboardingDate: new Date().toISOString(),
        phoneNumber: '',
        email: '',
        address: '',
        office: '',
        billingStatus: '',
        netPromoterScore: '', 
        clientEffortScores: '', 
        monthlyFeeSubscription: '',
        oneTimeOnboardingFee: '',
        address: '', 
      });
    } catch (error) {
      console.error('Error creating client:', error.response?.data || error.message);
      let errorMessage = 'Error creating client.';
      
      if (error.response?.data?.error && error.response.data.error.includes('already exists')) {
        errorMessage = 'This email is already being used by another client';
      } else {
        errorMessage = error.response?.data?.error || errorMessage;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleNewCompanyInputChange = (event) => {
    const { name, value } = event.target;
    setNewCompanyData({
      ...newCompanyData,
      [name]: value,
    });
  };

  const handleNewCompanySubmit = async () => {
    if (!newCompanyData.name.trim()) {
      toast.warning('Company name is required.');
      return;
    }

    const payload = {
      workspaceId: workspaceId,
      name: newCompanyData.name.trim(),
    };

    try {
      const response = await axios.post(`${API_URL}/utils/post-companies`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      toast.success('Company created successfully!');

      setCompanies((prevCompanies) => [...prevCompanies, response.data]);

      setIsNewCompanyOpen(false);
      setNewCompanyData({ name: '' });
    } catch (error) {
      console.error('Error creating company:', error.response?.data || error.message);
      toast.error('Error creating company.');
    }
  };

  // Helper function to render field label with optional/required indicator
  const renderFieldLabel = (label, name) => {
    const optionalFields = ['netPromoterScore', 'clientEffortScores', 'monthlyFeeSubscription', 'oneTimeOnboardingFee', 'phoneNumber', 'address'];
    const isRequired = !optionalFields.includes(name);
    
    return (
      <h2>
        {label} 
        {isRequired ? (
          <span style={{ color: 'red' }}>*</span>
        ) : (
          <span style={{ color: 'gray', fontSize: '0.8em', marginLeft: '5px' }}>
            (This field is optional)
          </span>
        )}
      </h2>
    );
  };

  return (
    <div className={`new-customer-form ${isOpen ? 'open' : ''}`}>
      <h1>New Customer Form</h1>

      {/* Two-column layout */}
      <div className="form-columns">
        {/* Left Column */}
        <div className="form-column">
          {[
            { label: 'First Name', name: 'firstName' },
            { label: 'Email', name: 'email' },
          ].map(({ label, name }) => (
            <div key={name} className="form-field">
              {renderFieldLabel(label, name)}
              <input
                type="text"
                name={name}
                value={formData[name]}
                onChange={handleInputChange}
                placeholder={`Enter ${label}`}
                style={errors[name] ? { borderColor: 'red' } : {}}
              />
            </div>
          ))}

          {/* Company Name Field with Button */}
          <div className="form-field company-field">
            {renderFieldLabel('Company Name', 'companyId')}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <FormControl fullWidth className="company-select" error={errors.companyId}>
                <Select
                  labelId="company-select-label"
                  name="companyId" 
                  value={formData.companyId || ''}
                  onChange={handleInputChange} 
                  label="Select Company"
                  displayEmpty
                  style={errors.companyId ? { borderColor: 'red' } : {}}
                >
                  <MenuItem value="" disabled>
                    Select Company
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name} 
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                style={{ marginLeft: '10px' }}
                onClick={() => setIsNewCompanyOpen(true)}
              >
                New Company
              </Button>
            </div>
          </div>

          {[
            { label: 'Net Promoter Score', name: 'netPromoterScore' },
            { label: 'Monthly Fee/Subscription', name: 'monthlyFeeSubscription' },
            { label: 'One Time Onboarding Fee', name: 'oneTimeOnboardingFee' },
          ].map(({ label, name }) => (
            <div key={name} className="form-field">
              {renderFieldLabel(label, name)}
              <input
                type="text"
                name={name}
                value={formData[name]}
                onChange={handleInputChange}
                placeholder={`Enter ${label}`}
              />
            </div>
          ))}

          {/* Dropdowns */}
          <div className="form-field">
            {renderFieldLabel('ICP Status', 'icp')}
            <FormControl fullWidth error={errors.icp}>
              <Select
                name="icp"
                value={formData.icp || ''}
                onChange={handleInputChange}
                displayEmpty
                style={errors.icp ? { borderColor: 'red' } : {}}
              >
                <MenuItem value="" disabled>
                  Select ICP
                </MenuItem>
                <MenuItem value="ICP I">ICP I</MenuItem>
                <MenuItem value="ICP II">ICP II</MenuItem>
                <MenuItem value="ICP III">ICP III</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        {/* Right Column */}
        <div className="form-column">
          {[
            { label: 'Last Name', name: 'lastName' },
            { label: 'Phone Number', name: 'phoneNumber' },
            { label: 'Address', name: 'address' },
            { label: 'Client Effort Score', name: 'clientEffortScores' },
          ].map(({ label, name }) => (
            <div key={name} className="form-field">
              {renderFieldLabel(label, name)}
              <input
                type="text"
                name={name}
                value={formData[name]}
                onChange={handleInputChange}
                placeholder={`Enter ${label}`}
                style={errors[name] ? { borderColor: 'red' } : {}}
              />
            </div>
          ))}

          {/* Dropdowns */}
          <div className="form-field">
            {renderFieldLabel('Touchpoint Frequency', 'touchpointFrequency')}
            <FormControl fullWidth error={errors.touchpointFrequency}>
              <Select
                name="touchpointFrequency"
                value={formData.touchpointFrequency || ''}
                onChange={handleInputChange}
                displayEmpty
                style={errors.touchpointFrequency ? { borderColor: 'red' } : {}}
              >
                <MenuItem value="" disabled>
                  Select Touchpoint
                </MenuItem>
                <MenuItem value="Weekly">Weekly</MenuItem>
                <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                <MenuItem value="Monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
          </div>

          <div className="form-field">
            {renderFieldLabel('Billing Status', 'billingStatus')}
            <FormControl fullWidth error={errors.billingStatus}>
              <Select
                name="billingStatus"
                value={formData.billingStatus || ''}
                onChange={handleInputChange}
                displayEmpty
                style={errors.billingStatus ? { borderColor: 'red' } : {}}
              >
                <MenuItem value="" disabled>
                  Select Billing Status
                </MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Paused">Paused</MenuItem>
                <MenuItem value="Past Due">Past Due</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <Button className="submit-button" onClick={handleSubmit}>
          Submit
        </Button>
      </div>

      <Dialog open={isNewCompanyOpen} onClose={() => setIsNewCompanyOpen(false)}>
        <DialogTitle>Create New Company</DialogTitle>
        <DialogContent sx={{
          width: '400px',
        }}>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            type="text"
            fullWidth
            value={newCompanyData.name}
            onChange={handleNewCompanyInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              backgroundColor: 'rgba(87, 42, 241, 1)',
              color: 'white',
              borderRadius: '37px',
              padding: '14px 25px',
              fontWeight: 500,
              fontFamily: "'Red Hat Text', sans-serif",
              textTransform: 'capitalize',
              width: 'auto',
              height: '40px',
              marginRight: '15px',
              marginBottom: '10px',
              '&:hover': {
                backgroundColor: 'rgba(87, 42, 241, 0.8)',
              },
            }}
            variant="contained"
            onClick={handleNewCompanySubmit}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default NewCustomerForm;